import { Currency, NativeCurrency, Token, WETH9 } from '@uniswap/sdk-core'
import invariant from 'tiny-invariant'

import { DEVNET_WHYDRA_ADDRESS, MAINNET_WHYDRA_ADDRESS, TESTNET_WHYDRA_ADDRESS } from './chainInfo'
import { SupportedChainId } from './chains'

export const NATIVE_CHAIN_ID = 'NATIVE'

// When decimals are not specified for an ERC20 token
// use default ERC20 token decimals as specified here:
// https://docs.openzeppelin.com/contracts/3.x/erc20
export const DEFAULT_ERC20_DECIMALS = 18

export const USDC_TESTNET = new Token(
  SupportedChainId.TESTNET,
  '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  6,
  'USDC',
  'USD//C'
)
// SAMVI Info: add tokens to your chain here that will stay at the top next to each other (other tokens can be imported from tokenList)
export const LYDRA = new Token(
  SupportedChainId.HYDRA,
  '0x0000000000000000000000000000000000001013',
  18,
  'LYDRA',
  'Liquid Hydra'
)
export const ETH = new Token(
  SupportedChainId.HYDRA,
  '0xD185683dfb59e26E269e00CE1DE75942c3d5fCD2',
  18,
  'ETH',
  'Ethereum'
)
export const WBTC = new Token(
  SupportedChainId.HYDRA,
  '0xb8043294eFf43bcD01BD33968c7ae9dbc6A4BF8B',
  8,
  'WBTC',
  'Wrapped Bitcoin'
)
export const DAI = new Token(SupportedChainId.HYDRA, '0xDf382759Ea2393a26d2F71924A4f69962ff0C1c5', 18, 'DAI', 'DAI')
export const USDC = new Token(SupportedChainId.HYDRA, '0xbBf6f2d2D462185dF545c744974b7Eb6ddadFcfd', 6, 'USDC', 'USDC')
export const USDT = new Token(SupportedChainId.HYDRA, '0x7b29E92ba491CD0AAcb64A363af2AfC2931Fd9F4', 6, 'USDT', 'USDT')
export const HYUSD = new Token(
  SupportedChainId.HYDRA,
  '0x71f16bcb805566f322a0904885d6147a76c249c5',
  18,
  'hyUSD',
  'hyUSD'
)
// ANI Token - swaps via Gateway Adapter
export const ANI = new Token(SupportedChainId.HYDRA, '0xa2f1C73FE7705c80f65D4db43529220d5B4fb154', 18, 'ANI', 'ANI')

// DEVNET HYDRA CHAIN
export const MYTOKEN1_DEV = new Token(
  SupportedChainId.DEVNET,
  '0xfbBa4908b44163698CC6622034F98727C346B361',
  18,
  'MT1',
  'MyToken1'
)
export const MYTOKEN2_DEV = new Token(
  SupportedChainId.DEVNET,
  '0x7eb8Fde1EdaA37Dd5c1bC3A241F81773559C1c8a',
  18,
  'MT2',
  'MyToken2'
)
export const LYDRA_DEV = new Token(
  SupportedChainId.DEVNET,
  '0x0000000000000000000000000000000000001013',
  18,
  'LYDRA',
  'Liquid Hydra'
)
// TESTNET HYDRA CHAIN
export const LYDRA_TESTNET = new Token(
  SupportedChainId.TESTNET,
  '0x0000000000000000000000000000000000001013',
  18,
  'LYDRA',
  'Liquid Hydra'
)
export const MYTOKEN1_TESTNET = new Token(
  SupportedChainId.TESTNET,
  '0x8dE2f2Acd7AE92aA2d5F13C95424a1b4C31CBcB2',
  18,
  'MT1',
  'MyToken1'
)
export const MYTOKEN2_TESTNET = new Token(
  SupportedChainId.TESTNET,
  '0xdd5C0811D1De34CC5B6A35Ae0A1A68EEfba2B4E2',
  18,
  'MT2',
  'MyToken2'
)
export const CKToken_TESTNET = new Token(
  SupportedChainId.TESTNET,
  '0x970C75bb31f1C48B35267C2a7871DAC05992Cb32',
  18,
  'CK',
  'CryptoKiddie'
)
export const DAI_TESTNET = new Token(
  SupportedChainId.TESTNET,
  '0xeae8aC8638597f9DD76a595A499b69a867153fED',
  18,
  'DAI',
  'Dai Stablecoin'
)

export const WRAPPED_NATIVE_CURRENCY: { [chainId: number]: Token | undefined } = {
  // SAMVI Info: add the wrapped native currency your chain here
  ...(WETH9 as Record<SupportedChainId, Token>),
  [SupportedChainId.HYDRA]: new Token(SupportedChainId.HYDRA, MAINNET_WHYDRA_ADDRESS, 18, 'WHYDRA', 'Wrapped Hydra'),
  [SupportedChainId.TESTNET]: new Token(
    SupportedChainId.TESTNET,
    TESTNET_WHYDRA_ADDRESS,
    18,
    'WHYDRA',
    'Wrapped Hydra'
  ),
  [SupportedChainId.DEVNET]: new Token(SupportedChainId.DEVNET, DEVNET_WHYDRA_ADDRESS, 18, 'WHYDRA', 'Wrapped Hydra'),
}

export function isHydraMain(chainId: number): chainId is SupportedChainId.HYDRA {
  return chainId === SupportedChainId.HYDRA
}

export function isTestnet(chainId: number): chainId is SupportedChainId.TESTNET {
  return chainId === SupportedChainId.TESTNET
}

export function isDevnet(chainId: number): chainId is SupportedChainId.DEVNET {
  return chainId === SupportedChainId.DEVNET
}

export function isHydra(
  chainId: number
): chainId is SupportedChainId.HYDRA | SupportedChainId.TESTNET | SupportedChainId.DEVNET {
  return (
    chainId === SupportedChainId.HYDRA || chainId === SupportedChainId.TESTNET || chainId === SupportedChainId.DEVNET
  )
}
class HydraNativeCurrency extends NativeCurrency {
  equals(other: Currency): boolean {
    return other.isNative && other.chainId === this.chainId
  }

  get wrapped(): Token {
    if (!isHydra(this.chainId)) throw new Error('Not Hydra Chain')
    const wrapped = WRAPPED_NATIVE_CURRENCY[this.chainId]
    invariant(wrapped instanceof Token)
    return wrapped
  }

  public constructor(chainId: number) {
    if (!isHydra(chainId)) throw new Error('Not Hydra Chain')
    super(chainId, 18, 'HYDRA', 'Hydra')
  }
}

const cachedNativeCurrency: { [chainId: number]: NativeCurrency | Token } = {}
export function nativeOnChain(_chainId: number): NativeCurrency | Token {
  const chainId = isHydra(_chainId) ? _chainId : SupportedChainId.HYDRA

  if (cachedNativeCurrency[chainId]) return cachedNativeCurrency[chainId]
  const nativeCurrency = new HydraNativeCurrency(chainId)

  return (cachedNativeCurrency[chainId] = nativeCurrency)
}

export const TOKEN_SHORTHANDS: {
  [shorthand: string]: { [chainId in SupportedChainId]?: string }
} = {
  USDC: {
    [SupportedChainId.TESTNET]: USDC_TESTNET.address,
  },
}
